/**
 * BrowserSection - Reusable collapsible section component.
 *
 * Uses native HTML <details>/<summary> for expand/collapse behavior.
 * Each instance maintains its own open/closed state without React state.
 */

import React from "react";
import clsx from "clsx";

type Props = {
    /** Section header label */
    label: string;

    /** Item count to display as badge */
    count: number;

    /** Whether the parent panel is expanded (not icon-only mode) */
    panelExpanded: boolean;

    /** Header icon element */
    icon: React.ReactNode;

    /** Icon color class for expanded icon display */
    iconColorClass: string;

    /** Background color class for selected/hovered items */
    accentColorClass?: string;

    /** Children to render when expanded */
    children: React.ReactNode;

    /** Dark mode flag */
    darkMode?: boolean;

    /** Whether the section should start open */
    defaultOpen?: boolean;
};

export default class BrowserSection extends React.Component<Props> {
    render() {
        const {
            label,
            count,
            panelExpanded,
            icon,
            iconColorClass,
            children,
            defaultOpen = false
        } = this.props;

        return (
            <details
                className="group border-b border-slate-100 dark:border-white/[0.04]"
                open={defaultOpen}
            >
                <summary
                    className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden"
                >
                    {/* Chevron */}
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open:rotate-90"
                    >
                        <path
                            d="M4,2 L8,6 L4,10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    </svg>

                    {panelExpanded ? (
                        <>
                            <span className={iconColorClass}>{icon}</span>
                            <span className="flex-1 text-xs font-medium">{label}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/50">
                                {count}
                            </span>
                        </>
                    ) : (
                        <span className={clsx(iconColorClass, "mx-auto")}>{icon}</span>
                    )}
                </summary>

                {panelExpanded && (
                    <div className="pb-2">
                        {children}
                    </div>
                )}
            </details>
        );
    }
}
